import { useMemo } from 'react'

import { useDataService, useDataState } from '~/data/data-context'
import { updatedAtOf } from '~/data/sync'
import { EMPTY_MEASUREMENTS, type WeighIn, type WeighInPhoto } from '~/data/weighins'

/**
 * The weigh-ins, read off the mirror the change feed fills — so a weigh-in saved in one place
 * is on the others in the same breath, and one saved on the phone is here the moment the feed
 * is pulled. Null until the feed has been pulled at least once, so nothing downstream claims
 * an empty day it has not read.
 *
 * The mirror belongs to the account that filled it and is dropped with the session, which is
 * what keeps the next account to sign in on this tab off the last one's body.
 */

const byNewest = (a: WeighIn, b: WeighIn) => (a.day < b.day ? 1 : -1)

/** The weigh-ins as they stand: null until the feed has landed, then every day newest first. */
export function useWeighIns(): WeighIn[] | null {
  const held = useDataState().weighins
  return useMemo(() => (held === null ? null : [...held].sort(byNewest)), [held])
}

/** Writing one into the mirror, which is where every screen reads it from. */
export function useWeighInWriter(): {
  keep: (saved: WeighIn) => void
  keepPhoto: (photo: WeighInPhoto) => void
  dropPhoto: (photo: WeighInPhoto) => void
} {
  const service = useDataService()
  const held = useDataState().weighins

  return {
    keep: (saved) => {
      void service.keep('weighin', saved.day, saved)
    },
    /** A photo lands on its day, growing the day's weigh-in or beginning it. */
    keepPhoto: (photo) => {
      const known = (held ?? []).find((weighIn) => weighIn.day === photo.day)
      const grown: WeighIn =
        known === undefined
          ? {
              day: photo.day,
              weightKg: null,
              measurements: { ...EMPTY_MEASUREMENTS },
              photos: [photo],
              createdAt: photo.createdAt,
              updatedAt: updatedAtOf(photo.createdAt),
            }
          : { ...known, photos: [...known.photos, photo] }
      void service.keep('weighin', grown.day, grown)
    },
    /** A removed photo leaves its day; the day's other holdings stay. */
    dropPhoto: (photo) => {
      const known = (held ?? []).find((weighIn) => weighIn.day === photo.day)
      if (known === undefined) {
        return
      }
      void service.keep('weighin', known.day, {
        ...known,
        photos: known.photos.filter((kept) => kept.id !== photo.id),
      })
    },
  }
}
